import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { CContainer, CRow, CCol, CButton } from '@coreui/react';
import api from '../../api/axiosInstance';
import { BASE_API_URL } from '../../config/apiConfig';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { Helmet } from 'react-helmet-async';

const CoursePage = () => {
  const { courseName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // SEO state setup
  const [seoData, setSeoData] = useState({
    metaTitle: '',
    metaKeywords: '',
    metaDescription: ''
  });

  const [bannerInfo, setBannerInfo] = useState({
    title: '',
    description: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchSeoData = async () => {
      try {
        const currentPath = window.location.href;
        const res = await api.get(`/api/seo/GetSeoByUrl?pageUrl=${encodeURIComponent(currentPath)}`, { skipAuthCheck: true });
        
        if (res.data) {
          setSeoData({
            metaTitle: res.data.metaTitle || res.data.MetaTitle,
            metaKeywords: res.data.metaKeywords || res.data.MetaKeywords,
            metaDescription: res.data.metaDescription || res.data.MetaDescription,
          });

          setBannerInfo({
            title: res.data.metaTitle || res.data.MetaTitle,
            description: res.data.metaDescription || res.data.MetaDescription
          });
        }
      } catch (err) {
        console.error("SEO Data Error:", err);
      }
    };

    fetchSeoData();
  }, [location.pathname]);

  const selectedCourse = courseName ? courseName.toLowerCase() : '';

  const courseInfo = {
    alpha: { title: 'Alpha: Free Access', desc: 'Questions Bank of Important and Tricky Questions with Solutions.' },
    beeta: { title: 'Beeta: PYQs', desc: 'Previous Year Questions Bank with Solutions of Competitive Exams.' },
    gamma: { title: 'Gamma: Book Solutions', desc: 'Solutions of Questions of Various TextBooks/Olympiad of Schools.' },
    delta: { title: 'Explore Delta Courses: Notes', desc: 'Get full NOTES of Mathematics Concepts with Examples, Questions & Solutions.' },
    sigma: { title: 'Sigma: All Access', desc: 'Get all course content in a single subscription.' },
  };

useEffect(() => {
  const fetchCourseCategories = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/admin/GetAllCategoriesBySection/${selectedCourse}`, { skipAuthCheck: true });
      
      // Handle both direct array or categoriesDetails object format
      const rawCategories = Array.isArray(res.data) 
        ? res.data 
        : res.data?.categoriesDetails || [];
        
      setCategories(rawCategories);
    } catch (err) {
      console.error('Error fetching categories for course:', err);
    } finally {
      setLoading(false);
    }
  };

  if (selectedCourse) {
    fetchCourseCategories();
  }
}, [selectedCourse]);

  const handleCourseAccess = (section, title) => {
    const formattedTitle = title.replace(/\s+/g, '-');
    
    if (section?.toLowerCase() === 'gamma') {
      navigate(`/${section}/${formattedTitle}/book-solutions/`);
    } else if (section?.toLowerCase() === 'delta') {
      navigate(`/${section}/${formattedTitle}/notes/`);
    } else {
      navigate(`/${section}/${formattedTitle}`);
    }
  };

  const currentInfo = courseInfo[selectedCourse] || {
    title: selectedCourse.toUpperCase(),
    desc: 'Course Content Overview',
  };

  return (
    <>
      <Helmet>
        <title>{seoData.metaTitle || "AmbiPie - Online Courses"}</title>
        <meta name="title" content={seoData.metaTitle} />
        <meta name="description" content={seoData.metaDescription} />
        <meta name="keywords" content={seoData.metaKeywords} />
      </Helmet>
      
      <div className="pb-5 bg-white">
        {/* Banner - Same Theme */}
        <div style={{ backgroundColor: 'rgb(0, 33, 71)', color: 'white', padding: '50px 0' }}>
          <CContainer className="text-center">
            <h1 className="fw-bold   mb-2" style={{ color: 'yellow' }}>{currentInfo.title}</h1>
            <p className="lead" style={{ opacity: 0.9 }}>{currentInfo.desc}</p>
          </CContainer>
        </div>

        <CContainer className="mt-5">
          {loading ? (
            <div className="text-center py-5">
              <h4>Loading Course Content...</h4>
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-5">
              <h5>No categories are available for this course.</h5>
              <CButton color="primary" className="mt-3" onClick={() => navigate('/')}>
                Back to Home
              </CButton>
            </div>
          ) : (
            /* STANDARD COURSE CARD DESIGN FOR ALL COURSES (ALPHA, BEETA, GAMMA, DELTA) */
            <CRow className="g-4">
              {categories.map((item, idx) => (
                <CCol md={4} sm={6} key={idx}>
                  <div
                    className="border border-dark text-center bg-white shadow-sm h-100"
                    style={{ borderRadius: '15px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
                  >
                    <div className="border-bottom border-dark py-2.5 fw-bold bg-light text-dark">
                      {item.title}
                    </div>
                    <div style={{ flex: 1, overflow: 'hidden', height: '160px', backgroundColor: '#f0f0f0' }}>
                      <LazyLoadImage
                        alt={item.title}
                        effect="blur"
                        src={`${BASE_API_URL}${item.imagePath}`}
                        wrapperProps={{
                          style: { transitionDelay: '1s', height: '100%', width: '100%', display: 'block' },
                        }}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        placeholderSrc="https://placehold.co/400x100?text=Loading..."
                      />
                    </div>
                    <div
                      className="border-top border-dark py-2.5 fw-bold"
                      style={{ cursor: 'pointer', backgroundColor: '#f8f9fa', transition: '0.3s' }}
                      onMouseOver={(e) => (e.target.style.backgroundColor = '#e9ecef')}
                      onMouseOut={(e) => (e.target.style.backgroundColor = '#f8f9fa')}
                      onClick={() => handleCourseAccess(selectedCourse, item.title.toLowerCase())}
                    >
                      {item.buttonText}
                    </div>
                  </div>
                </CCol>
              ))}
            </CRow>
          )}

          <div className="text-center mt-5">
            <CButton color="secondary" onClick={() => navigate('/')}>
              Back to Home
            </CButton>
          </div>
        </CContainer>
      </div>
    </>
  );
};

export default CoursePage;